type ValidationInput = unknown;

interface ValidationError {
  message: string;
  [key: string]: unknown;
}

interface IValidator {
  validator: (input: ValidationInput) => boolean;
  getErrorMessage: (propertyName: string, value: unknown) => string;
}

const throwErrors = (errors: ValidationError[] | string[]) => {
  if (errors.length > 0) {
    throw new Error(errors.join("\n"));
  }
};

const getValueMessage = (value: unknown): string => `Actual value: ${value}. Type: ${typeof value}`;

const getValue = (input: ValidationInput, propertyName: string): unknown => {
  if (input instanceof Object && input !== null) {
    const obj = input as Record<string, unknown>;
    return obj[propertyName];
  }
  return typeof input === "function" ? "function" : input;
};

export default class Validator {
  private validators: Array<IValidator>;

  constructor() {
    this.validators = [];
  }

  isPositiveInteger() {
    this.validators.push({
      validator: (input): boolean => typeof input === "number" && Number.isInteger(input) && input > 0,
      getErrorMessage: (propertyName: string, value: unknown): string =>
        `"${propertyName}" should be a positive number. ${getValueMessage(value)}`,
    });
    return this;
  }

  isPositiveIntegerOrZero() {
    this.validators.push({
      validator: (input): boolean => {
        if (typeof input === "number") {
          return Number.isInteger(input) && input >= 0;
        }
        if (typeof input === "string") {
          const parsed = parseInt(input);
          return !isNaN(parsed) && parsed >= 0;
        }
        return false;
      },
      getErrorMessage: (propertyName: string, value: unknown): string =>
        `"${propertyName}" should be positive number or zero. ${getValueMessage(value)}`,
    });
    return this;
  }

  isFunction() {
    this.validators.push({
      validator: (input): boolean => typeof input === "function",
      getErrorMessage: (propertyName: string, value: unknown): string =>
        `"${propertyName}" should be a function. ${getValueMessage(value)}`,
    });
    return this;
  }

  isArray() {
    this.validators.push({
      validator: (input): boolean => Array.isArray(input),
      getErrorMessage: (propertyName: string, value: unknown): string =>
        `"${propertyName}" should be an array. ${getValueMessage(value)}`,
    });
    return this;
  }

  isDefinedInObject(obj: Record<string, unknown>) {
    // use keys without numeric because converting enums give additional digital keys
    // @see https://www.crojach.com/blog/2019/2/6/getting-enum-keys-in-typescript
    const keys = Object.keys(obj).filter((x) => !this.isNumeric(x));
    this.validators.push({
      validator: (input): boolean => keys.some((item) => obj[item] === input),
      getErrorMessage: (propertyName: string, value: unknown): string =>
        `"${propertyName}" should be one of following: [${keys.map((key) => obj[key]).join(", ")}]. ${getValueMessage(
          value,
        )}`,
    });
    return this;
  }

  isNumeric = (val: string): boolean => {
    return !isNaN(parseInt(val)) && parseInt(val) >= 0;
  };

  isNotNullOrUndefined() {
    this.validators.push({
      validator: (input): boolean => input !== null && input !== undefined,
      getErrorMessage: (propertyName: string, value: unknown): string =>
        `"${propertyName}" is null or undefiend. ${getValueMessage(value)}`,
    });
    return this;
  }

  validate(input: ValidationInput, propertyName: string): string[] {
    let errors: string[] = [];
    this.validators.forEach((item) => {
      if (!item.validator(input)) {
        errors.push(item.getErrorMessage(propertyName, getValue(input, propertyName)));
      }
    });
    return errors;
  }

  validateAndThrow(input: ValidationInput, propertyName: string): void {
    const errors = this.validate(input, propertyName);
    throwErrors(errors);
  }

  static validateSchema<T extends object>(validationSchema: Record<string, Validator>, input: T): string[] {
    let errors: string[] = [];
    Object.keys(validationSchema).forEach((key) => {
      let propertyErrors = validationSchema[key].validate((input as Record<string, unknown>)[key], key);
      errors = errors.concat(propertyErrors);
    });
    return errors;
  }

  static validateSchemaAndThrow<T extends object>(validationSchema: Record<string, Validator>, input: T): void {
    const errors = Validator.validateSchema(validationSchema, input);
    throwErrors(errors);
  }

  static create(): Validator {
    return new Validator();
  }
}
